export enum ExitSurveyTab {
    ANALYTICS = "analytics",
    EXIT_SURVEY_LIST = "exit-survey-list"
  }

export enum ExitSurveyStatus {
  NEEDS_ATTENTION = "needs-attention",
  COMPLETED = "completed",
  PENDING_HRBP = "pending-hrbp",
  PENDING_BOTH = "pending-both",
  PENDING_EMPLOYEE = "pending-employee",
  PENDING_MAIN = "pending-main"
}

export interface ExitSurveyStats {
  total_surveys: number;
  completed: number;
  pending_hrbp: number;
  pending_both: number;
  pending_employee: number;
  pending_main: number;
}

export interface ExitSurveyFilter {
  timeRange: string;
  branch: string;
  job: string;
  rankBy: string;
  showCompleted?: boolean;
  showPendingHRBP?: boolean;
  showPendingBoth?: boolean;
  showPendingEmployee?: boolean;
}

export interface ExitSurveyEmployeeInfo {
  employee_id: number;
  name: string;
  category: string | null;
  office_location: string | null;
  is_deleted: boolean;
  job_title: string;
}

export interface ExitSurveyRow {
  supervisor: string;
  survey_sent_date: string;
  status: ExitSurveyStatus;
  employee_info: ExitSurveyEmployeeInfo;
  tenure: string;
  escalation_reason: string;
  assignee: string;
  escalation_time: string;
  survey_type: string;
  survey_title: string;
  contains_hrbp: boolean;
  exit_type: string;
  exit_date: string;
}

export interface ExitSurveyQuestion {
  id: string;
  question: string;
  type: 'single' | 'multiple' | 'text';
  answer?: string | string[];
  options?: string[];
}

// API Response Interfaces
export interface ExitSurveyResponse {
  response_id: number;
  type: string;
  order: number;
  category: string;
  question: string;
  response: string;
  sentiment: string | null;
  created_at: string;
  updated_at: string;
}

export interface ExitSurveyEmployeeData {
  employee_id: number;
  title: string;
  updated_at: string;
  post_termination: boolean;
  completed: boolean;
  responses: ExitSurveyResponse[];
  typeform_uri?: string;
}

export interface ExitSurveyAPIResponse {
  termination_date: string;
  hrbp: ExitSurveyEmployeeData;
  employee: ExitSurveyEmployeeData;
}

export interface ExitSurveyData {
  employeeSurvey: {
    status: 'completed' | 'pending' | 'not_started';
    questions: ExitSurveyQuestion[];
  };
  hrbpSurvey: {
    status: 'completed' | 'pending' | 'not_started';
    surveyLink?: string;
    exitInterviewStatus?: string;
    employeeReasons?: string[];
    comments?: string;
  };
  stats?: ExitSurveyStats;
  filters?: ExitSurveyFilter;
  rows?: ExitSurveyRow[];
}

// Mock data for demonstration - Employee pending, HRBP completed
export const mockExitSurveyData: ExitSurveyData = {
  employeeSurvey: {
    status: 'pending',
    questions: [
      {
        id: 'q1',
        question: 'What is your primary reason for leaving?',
        type: 'single',
        options: ['Training or education', 'Career advancement', 'Compensation', 'Work-life balance', 'Management issues', 'Other']
      },
      {
        id: 'q2',
        question: 'Please select any other factors that contributed to your decision.',
        type: 'multiple',
        options: ['Leaving the workforce', 'Leadership', 'Compensation', 'Training or education', 'Leaving the hospice field', 'Work schedule', 'Patient caseload', 'Team dynamics', 'Company culture']
      },
      {
        id: 'q3',
        question: 'Do you have any additional feedback or concerns you would like to share?',
        type: 'text'
      }
    ]
  },
  hrbpSurvey: {
    status: 'completed',
    exitInterviewStatus: 'Exit Interview Completed',
    employeeReasons: ['Leaving the workforce', 'Leadership', 'Compensation', 'Training or education', 'Leaving the hospice field', 'Work schedule', 'Patient caseload', 'Company culture'],
    comments: 'Talked with Khloe. She shared that while she enjoyed aspects of the role, the patient caseload and lack of leadership support made it difficult to stay. She also mentioned compensation and scheduling as contributing factors. Appreciated the opportunity to provide feedback and expressed interest in returning if circumstances change.'
  }
};

// Mock data for completed employee survey
export const mockCompletedEmployeeData: ExitSurveyData = {
  employeeSurvey: {
    status: 'completed',
    questions: [
      {
        id: 'q1',
        question: 'What is your primary reason for leaving?',
        type: 'single',
        answer: 'Training or education',
        options: ['Training or education', 'Career advancement', 'Compensation', 'Work-life balance', 'Management issues', 'Other']
      },
      {
        id: 'q2',
        question: 'Please select any other factors that contributed to your decision.',
        type: 'multiple',
        answer: ['Leaving the workforce', 'Leadership', 'Compensation', 'Training or education', 'Leaving the hospice field', 'Work schedule', 'Patient caseload'],
        options: ['Leaving the workforce', 'Leadership', 'Compensation', 'Training or education', 'Leaving the hospice field', 'Work schedule', 'Patient caseload', 'Team dynamics', 'Company culture']
      },
      {
        id: 'q3',
        question: 'Do you have any additional feedback or concerns you would like to share?',
        type: 'text',
        answer: 'No additional feedback'
      }
    ]
  },
  hrbpSurvey: {
    status: 'pending',
    surveyLink: 'https://example.com/survey-link'
  }
};

// Utility functions for status handling
export const getStatusVariant = (status: string) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'pending':
      return 'pending';
    case 'not_started':
      return 'no-data';
    default:
      return 'no-data';
  }
};

export const getStatusText = (status: string) => {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'pending':
      return 'Pending';
    case 'not_started':
      return 'Not Started';
    default:
      return 'Unknown';
  }
}; 

export enum EscalationReason {
  LOW_SENTIMENT_SCORE = "LOW_SENTIMENT_SCORE",
  LOW_SENTIMENT_SCORE_FLAGGED_QUESTION = "LOW_SENTIMENT_SCORE_FLAGGED_QUESTION",
  INCOMPLETE_SURVEY = "INCOMPLETE_SURVEY"
}
